import { useLocation } from 'react-router-dom';
import { ArrowRight, ChevronDown, Check } from 'lucide-react';
import { BUSINESS } from '../data/business';
import { isEnglishPath } from '../utils/i18nRoutes';
import { siteContent } from '../config/siteContent';

export default function Hero() {
  const location = useLocation();
  const isEn = isEnglishPath(location.pathname);

  const eyebrow = isEn ? 'AI CONSULTING FOR COMPANIES IN AUSTRIA' : 'KI-BERATUNG FÜR UNTERNEHMEN IN ÖSTERREICH';
  const h1Part1 = isEn ? 'AI Consulting for' : 'KI-Beratung für';
  const h1Part2 = isEn ? 'mid-sized companies in Austria' : 'mittelständische Unternehmen in Österreich';
  const subLine = isEn ? 'AI automation and cost optimization from Vienna' : 'KI-Automatisierung und Kostenoptimierung aus Wien';
  const sublineBoldPart = isEn ? 'OptiAI automates your business processes with AI and reduces your operational costs' : 'OptiAI automatisiert Ihre Geschäftsprozesse mit KI und senkt Ihre Betriebskosten';

  const primaryCta = isEn ? 'Book a free consultation' : 'Kostenloses Erstgespräch vereinbaren';
  const secondaryCta = isEn ? 'View services' : 'Leistungen ansehen';

  const cardTitle = isEn ? 'Free consultation' : 'Kostenloses Erstgespräch';
  const cardItems = isEn
    ? ['Free and non-binding', 'Response within one business day', 'By phone or on-site in Vienna']
    : ['Kostenlos und unverbindlich', 'Rückmeldung innerhalb eines Werktages', 'Telefonisch oder vor Ort in Wien'];
  const cardButton = isEn ? 'Request now' : 'Jetzt anfragen';
  const cardPhoneLabel = isEn ? 'or call:' : 'oder anrufen:';

  const heroImage = siteContent.heroImage;

  return (
    <section className="relative bg-dark text-white pt-32 pb-24 px-6 overflow-hidden">
      {heroImage && (
        <div className="absolute inset-0">
          <img src={heroImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark via-dark/80 to-transparent" />
        </div>
      )}

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left column */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
              <span className="text-xs font-semibold tracking-wide text-accent-light">{eyebrow}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] font-medium leading-[1.15] mb-4">
              {h1Part1}{' '}
              <span className="text-accent-light">{h1Part2}</span>
            </h1>

            <p className="font-heading text-lg text-gray-400 mb-6 font-medium">
              {subLine}
            </p>

            <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-xl">
              <strong className="font-semibold text-white">{sublineBoldPart}</strong>
              {isEn
                ? ` — for companies with 50 to 500 employees in Vienna and across the DACH region.`
                : ` – für Unternehmen mit 50 bis 500 Mitarbeitern in Wien und im gesamten DACH-Raum.`}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#anfrage" className="btn-primary text-base">
                {primaryCta}
                <ArrowRight className="w-5 h-5" />
              </a>
              <a href="#services" className="btn-ghost-dark text-base">
                {secondaryCta}
              </a>
            </div>
          </div>

          {/* Right column: consultation card */}
          <div className="lg:justify-self-end w-full max-w-md">
            <div className="bg-white rounded-card p-8 shadow-xl">
              <h3 className="font-heading text-xl font-bold text-dark mb-6">{cardTitle}</h3>
              <ul className="space-y-4 mb-6">
                {cardItems.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center mt-0.5">
                      <Check className="w-3.5 h-3.5 text-accent" />
                    </span>
                    <span className="text-dark text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <a href="#anfrage" className="btn-dark w-full text-base">
                {cardButton}
                <ArrowRight className="w-4 h-4" />
              </a>
              <p className="text-center text-sm text-muted mt-4">
                {cardPhoneLabel}{' '}
                <a href={BUSINESS.phoneHref} className="font-semibold text-primary hover:underline">
                  {BUSINESS.phone}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* White tab with chevron at bottom edge */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex flex-col items-center">
        <div className="bg-white px-6 py-2 rounded-t-xl">
          <ChevronDown className="w-6 h-6 text-dark" />
        </div>
      </div>
    </section>
  );
}
